import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {UserDefinedWidget} from '@models/pages/user-defined-page/user-defined-widget';
import {UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {Machine} from '@models/machine/machine';
import {MetaData} from '@models/adminstration/meta-data';
import {NotificationService} from '@services/notification/notification.service';
import {TranslateService} from '@ngx-translate/core';
import {MachineService} from '@services/administration/machine/machine.service';
import {LoadingIndicatorService} from '@services/loading-indicator/loading-indicator.service';
import {FileTypes} from '@constants/file-types';
import {UserDefinedPageService} from '@services/pages/user-defined-page/user-defined-page.service';
import {ImageDetails} from '@models/pages/user-defined-page/image-widget/image-details';
import {ImageSourceType} from '@models/pages/user-defined-page/user-defined-chart-details';
import {UserDefinedWidgetType} from '@constants/pages/user-defined-page/user-defined-widget-type';
import {MetadataElasticSearchService} from '@services/elastic-search/metadata/metadata-elastic-search.service';
import {ElasticSearchResponseEntity} from '@models/elastic-search/response/elastic-search-response-entity';

@Component({
  selector: 'scitis-image-widget-configuration',
  templateUrl: './image-widget-configuration.component.html',
  styleUrls: ['./image-widget-configuration.component.scss'],
  standalone: false
})
export class ImageWidgetConfigurationComponent implements OnInit {

  @Input()
  machine: Machine = new Machine();

  @Input()
  userDefinedWidget: UserDefinedWidget = new UserDefinedWidget();

  @Output()
  isConfigurationValid: EventEmitter<boolean> = new EventEmitter<boolean>();

  public imageWidgetForm: UntypedFormGroup = new UntypedFormGroup({});
  public metaDataFields: MetaData[] = [];
  public customStoragePathEnabled: boolean = true;
  public isFileUploadDisabled: boolean = false;
  public isCSUploadDisabled: boolean = true;
  public imageFileTypes: FileTypes[] = [FileTypes.PNG, FileTypes.JPG];
  public outcomeOfUploadingPicture: any;
  public selectedImageSourceType: ImageSourceType = ImageSourceType.IMAGE_UPLOAD;
  public defaultCloudStorageBucket = '';
  public maxFileSize: number = 2500000;
  public isLoadingImageFromMetaField: boolean = false;
  public isLoadingImagePreview: boolean = false;
  public ImageSourceType = ImageSourceType;

  private COMPONENT_NAME: string = 'ImageWidgetConfigurationComponent';
  private uploadedFileName: string = '';
  private uploadToCloudStorageSuccessMessage = '';
  private uploadToCloudStorageErrorMessage = '';
  private getDefaultProjectErrorMessage = '';
  private loadErrorMessage = '';

  constructor(private notification: NotificationService,
              private translate: TranslateService,
              private loadingIndicator: LoadingIndicatorService,
              private userDefinedPageService: UserDefinedPageService,
              private machineService: MachineService,
              private elasticMetaDataService: MetadataElasticSearchService) {
    this.setMessages();
  }

  get formControls() {
    return this.imageWidgetForm.controls;
  }

  public get isImageWidget(): boolean {
    return this.userDefinedWidget.widgetDetails.widgetType === UserDefinedWidgetType.IMAGE;
  }

  get isImage(): boolean {
    return this.selectedImageSourceType === ImageSourceType.IMAGE_UPLOAD;
  }

  get isMapping(): boolean {
    return this.selectedImageSourceType === ImageSourceType.METAFIELD;
  }

  public get imageName(): string {
    if (!this.userDefinedWidget || !this.userDefinedWidget.userDefinedChartDetails || !this.userDefinedWidget.userDefinedChartDetails.imageWidgetImageNameOrMetaKey) {
      return '';
    }
    return this.userDefinedWidget.userDefinedChartDetails.imageWidgetImageNameOrMetaKey.toBold();
  }

  ngOnInit(): void {
    this.getMachineMetaData();
    this.generateImageWidgetForm();
    this.getDefaultBucket();
    this.loadImage();
  }

  public selectImageType(imageSourceType: ImageSourceType) {
    this.selectedImageSourceType = imageSourceType;
    this.userDefinedWidget.userDefinedChartDetails.imageSourceType = imageSourceType;
    this.updateSelectedMetaDataFieldValidators();
  }

  public onFileUploadComplete(content: string | ArrayBuffer | null) {
    this.formControls.image.setValue(content);
    this.userDefinedWidget.userDefinedChartDetails.imageSourceType = ImageSourceType.IMAGE_UPLOAD;
    this.isCSUploadDisabled = false;

    this.isConfigurationValid.emit(false);
  }

  public onFileNameUploadComplete(content: string) {
    this.uploadedFileName = content;
  }

  public onFileUploadError(error: ProgressEvent<FileReader>) {
    this.outcomeOfUploadingPicture = this.translate.instant('There was an error uploading the image') + '....';
    this.notification.error('Error uploading image', error, this.COMPONENT_NAME, true);
  }

  public deleteImage(): void {
    this.formControls.image.setValue(null);
    this.userDefinedWidget.userDefinedChartDetails.imageWidgetImageNameOrMetaKey = '';
    this.isCSUploadDisabled = true;
    this.isConfigurationValid.emit(this.imageWidgetForm.valid);
  }

  public onUploadToCloudStorage(): void {
    this.loadingIndicator.show();
    this.userDefinedPageService.saveImage(
        this.machine,
        this.userDefinedWidget.userDefinedChartDetails.imageWidgetBucketName, this.uploadedFileName, String(this.formControls.image.value)
    ).subscribe(
        {
          next: () => {
            const message = this.uploadToCloudStorageSuccessMessage.replace('%s', this.uploadedFileName.toBold());
            this.userDefinedWidget.userDefinedChartDetails.imageWidgetImageNameOrMetaKey = this.uploadedFileName;
            this.isConfigurationValid.emit(this.imageWidgetForm.valid);
            this.notification.success(message, this.COMPONENT_NAME, true);
            this.loadingIndicator.hide();
          }, error: error => {
            const message = this.uploadToCloudStorageErrorMessage.replace('%s', this.userDefinedWidget.userDefinedChartDetails.imageWidgetImageNameOrMetaKey.toBold());
            this.notification.error(message, error, this.COMPONENT_NAME, true);
            this.loadingIndicator.hide();
          }
        }
    );
  }

  public onCustomStoragePathChange(): void {
    this.userDefinedWidget.userDefinedChartDetails.imageWidgetBucketName = this.formControls.customStoragePath.value;
    this.isConfigurationValid.emit(this.imageWidgetForm.valid);
  }

  public onMetaFieldSelected(selectedMetadataField: MetaData | undefined): void {

    if (!selectedMetadataField || !selectedMetadataField.key) {
      return;
    }

    this.isLoadingImageFromMetaField = true;
    this.userDefinedWidget.userDefinedChartDetails.imageWidgetImageNameOrMetaKey = selectedMetadataField.key;
    const bucketPath = this.userDefinedWidget.userDefinedChartDetails.imageWidgetBucketName + '/';
    this.userDefinedPageService.getImage(
        this.machine,
        ImageSourceType.METAFIELD,
        bucketPath,
        selectedMetadataField.key
    ).subscribe(
        {
          next: result => {
            this.setImageProperties(result);
            this.isLoadingImageFromMetaField = false;
            this.isConfigurationValid.emit(this.imageWidgetForm.valid);
          }, error: error => {
            console.log(error);
            this.setImageProperties(null);
            this.notification.error(this.loadErrorMessage, error, this.COMPONENT_NAME, true);
            this.isLoadingImageFromMetaField = false;
          }
        });

  }

  public onResetCustomStoragePath() {
    this.formControls.customStoragePath.setValue(this.defaultCloudStorageBucket);
  }

  private getMachineMetaData() {
    if (!this.machine.id) {
      return;
    }
    this.loadingIndicator.show();
    this.elasticMetaDataService.listAllMachineMetaData(this.machine.id).subscribe(
        {
          next: (elasticResponse: ElasticSearchResponseEntity) => {
            const machineMetaData = elasticResponse?.result?.data;
            if (machineMetaData) {
              this.filterMetaData(machineMetaData);
            }
            this.loadingIndicator.hide();
          }, error: (error) => {
            this.loadingIndicator.hide();
            console.log(error);
          }
        });
  }

  private filterMetaData(machineMetaData: MetaData[]) {
    for (const metaDataField of machineMetaData) {
      if (metaDataField.key && (metaDataField.value?.endsWith('.png') || metaDataField.value?.endsWith('.jpg'))) {
        this.metaDataFields = [...this.metaDataFields, metaDataField];
      }
    }
  }

  private getDefaultBucket() {
    this.loadingIndicator.show();
    this.machineService.getDefaultGoogleProjectId().subscribe(
        {
          next: result => {
            this.defaultCloudStorageBucket = result[0] + '.appspot.com/images';
            this.userDefinedWidget.userDefinedChartDetails.imageWidgetBucketName = this.defaultCloudStorageBucket;
            this.formControls.customStoragePath.setValue(this.defaultCloudStorageBucket);
            this.formControls.customStoragePath.enable();
            this.loadingIndicator.hide();
          }, error: error => {
            console.log(error);
            const message = this.getDefaultProjectErrorMessage.replace('%s', this.userDefinedWidget.userDefinedChartDetails.imageWidgetImageNameOrMetaKey.toBold());
            this.notification.error(message, error, this.COMPONENT_NAME, true);
            this.loadingIndicator.hide();
          }
        }
    );
  }

  private loadImage(): void {
    if (this.userDefinedWidget.userDefinedChartDetails.imageSourceType === ImageSourceType.METAFIELD) {
      this.isLoadingImageFromMetaField = true;
    }
    if (this.userDefinedWidget.userDefinedChartDetails.imageWidgetBucketName &&
        this.userDefinedWidget.userDefinedChartDetails.imageWidgetImageNameOrMetaKey
        && this.userDefinedWidget.userDefinedChartDetails.imageSourceType) {
      this.isLoadingImagePreview = true;
      const bucketPath = this.userDefinedWidget.userDefinedChartDetails.imageWidgetBucketName + '/';
      this.userDefinedPageService.getImage(
          this.machine,
          this.userDefinedWidget.userDefinedChartDetails.imageSourceType,
          bucketPath,
          this.userDefinedWidget.userDefinedChartDetails.imageWidgetImageNameOrMetaKey
      ).subscribe(
          {
            next: result => {
              this.setImageProperties(result);
              this.isLoadingImageFromMetaField = false;
              this.isLoadingImagePreview = false;
              this.isConfigurationValid.emit(this.imageWidgetForm.valid);
            }, error: error => {
              console.log(error);
              this.setImageSourceType();
              this.notification.error(this.loadErrorMessage, error, this.COMPONENT_NAME, true);
              this.isLoadingImageFromMetaField = false;
              this.isLoadingImagePreview = false;
            }
          }
      );
    }
  }

  private generateImageWidgetForm(): void {
    this.imageWidgetForm = new UntypedFormGroup({
      customStoragePath: new UntypedFormControl({value: '', disabled: true}),
      image: new UntypedFormControl(null, [Validators.required]),
      selectedMetaDataField: new UntypedFormControl()
    });
    if (this.userDefinedWidget.userDefinedChartDetails.imageSourceType == ImageSourceType.METAFIELD &&
        this.userDefinedWidget.userDefinedChartDetails.imageWidgetImageNameOrMetaKey) {
      this.formControls.selectedMetaDataField.setValue(this.userDefinedWidget.userDefinedChartDetails.imageWidgetImageNameOrMetaKey);
    }
    this.updateValidators();
  }

  private updateValidators(): void {
    this.updateCustomStoragePathValidators();
    this.updateSelectedMetaDataFieldValidators();
  }

  private updateCustomStoragePathValidators(): void {
    if (this.customStoragePathEnabled) {
      this.formControls.customStoragePath.setValidators([Validators.required]);
      this.formControls.customStoragePath.updateValueAndValidity();
    } else {
      this.formControls.customStoragePath.setValidators([]);
      this.formControls.customStoragePath.updateValueAndValidity();
    }
    this.isConfigurationValid.emit(this.imageWidgetForm.valid);
  }

  private updateSelectedMetaDataFieldValidators(): void {
    if (this.isMapping) {
      this.formControls.selectedMetaDataField.setValidators([Validators.required]);
      this.formControls.selectedMetaDataField.updateValueAndValidity();
    } else {
      this.formControls.selectedMetaDataField.setValidators([]);
      this.formControls.selectedMetaDataField.updateValueAndValidity();
    }
    this.isConfigurationValid.emit(this.imageWidgetForm.valid);
  }

  private setMessages(): void {
    this.uploadToCloudStorageSuccessMessage = this.translate.instant('File %s was uploaded to Cloud Storage.');
    this.uploadToCloudStorageErrorMessage = this.translate.instant('File %s was not uploaded to Cloud Storage');
    this.loadErrorMessage = this.translate.instant('Error loading image for image widget.');
  }

  private setImageProperties(imageDetails: ImageDetails | null): void {
    if (!imageDetails || !imageDetails.imageContent || imageDetails.imageContent.isNullOrWhitespace()) {
      this.formControls.image.setValue(null);
    } else {
      this.formControls.image.setValue('data:image/png;base64,' + imageDetails.imageContent);
    }

    this.formControls.customStoragePath.setValue(this.userDefinedWidget.userDefinedChartDetails.imageWidgetBucketName);

    this.setImageSourceType();
  }

  private setImageSourceType(): void {
    if (this.userDefinedWidget.userDefinedChartDetails.imageSourceType) {
      this.selectedImageSourceType = this.userDefinedWidget.userDefinedChartDetails.imageSourceType;
    }
    if (this.selectedImageSourceType === ImageSourceType.METAFIELD) {
      this.metaDataFields.forEach(meta => {
        if (meta.key === this.userDefinedWidget.userDefinedChartDetails.imageWidgetImageNameOrMetaKey) {
          this.formControls.selectedMetaDataField.setValue(meta);
        }
      });
    }
  }
}
